import { useState } from 'react'
import {
  ButtonBase,
  IconButton,
  InputBase,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { FileDownloadOutlined as DownloadIcon } from '@mui/icons-material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useToast } from '@/shared/contexts/ToastContext'
import { RADIUS } from '@/shared/styles/radii'
import { scaledFontSize } from '@/shared/styles/typography'
import { downloadBlob } from '@/shared/utils/downloadBlob'

import {
  downloadNutritionReportXlsx,
  type NutritionDestination,
  type NutritionPatientId,
  type NutritionCustomer,
  type NutritionReport,
} from './api'
import { fieldInputSx } from '@/apps/latest/shared/styles/inlineInput'
import { useUpdateNutritionCustomer } from './useNutritionCustomers'

// 出す宛先 (back の services/excel.py の SHEETS と同じ 3 つ)。
const NUTRITION_DESTINATIONS: {
  key: NutritionDestination
  label: string
}[] = [
  { key: 'medical', label: '医療機関用' },
  { key: 'pharmacy', label: '薬局用' },
  { key: 'cafe', label: 'カフェ用' },
]

// 店舗名の突き合わせ用の形 (back の store_key と同じ)。末尾の「店」と前後の空白を落とす。
// 報告書の依頼店舗は「串戸」、お客様の情報で打つ店舗は「串戸店」になりがちで、
// 字面の一致に頼ると黙って空欄になる。
const storeKey = (store: string): string =>
  store.trim().replace(/店$/, '').trim()

// 依頼店舗の患者 ID (back の patient_id_for と同じ突き合わせ)。
export const patientIdFor = (
  patientIds: NutritionPatientId[],
  store: string
): string =>
  patientIds.find((row) => storeKey(row.store) === storeKey(store))?.id ?? ''

// 依頼店舗の行を差し替える。空にしたら行を消す。他の店舗の行はそのまま。
export const withPatientId = (
  patientIds: NutritionPatientId[],
  store: string,
  id: string
): NutritionPatientId[] => {
  const key = storeKey(store)
  const rest = patientIds.filter((row) => storeKey(row.store) !== key)
  return id.trim() ? [...rest, { store: store.trim(), id: id.trim() }] : rest
}

// 栄養カウンセリング報告書_薬局用_山田花子_20260918.xlsx
export const exportFilename = (
  label: string,
  name: string,
  recordedOn: string
): string =>
  [
    '栄養カウンセリング報告書',
    label,
    name.replace(/\s/g, ''),
    recordedOn.replace(/-/g, ''),
  ]
    .filter(Boolean)
    .join('_') + '.xlsx'

interface Props {
  eventId: string
  report: NutritionReport
  // 患者 ID の持ち主 (打った ID はお客様に残り、次の記録からも埋まる)
  customer: NutritionCustomer
}

// 出す: 回の行の右端に置くダウンロードのアイコン (選んだ回を出す。ADR-0086 決定 7。報告書の下だと 44 行の格子を
// 送らないと届かない)。押すと popover に宛先 3 行、行を押すとその宛先で落ちる。確認も「出力済み」も持たない。
// design-book 04-apps/kakeru.md「管理栄養士の記録」。
export const NutritionExportButton = ({ eventId, report, customer }: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  return (
    <>
      <Tooltip title="ダウンロード">
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="報告書をダウンロード"
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={anchor != null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              width: 320,
              borderRadius: `${RADIUS.lg}px`,
              p: 0.5,
              bgcolor: colorTokens.bg.default,
              border: `1px solid ${colorTokens.border.subtle}`,
              boxShadow: colorTokens.shadow.popover,
            },
          },
        }}
      >
        <ExportRows
          eventId={eventId}
          report={report}
          customer={customer}
          onDone={() => setAnchor(null)}
        />
      </Popover>
    </>
  )
}

// popover の中身。行の右値にその宛先で差し込まれる値。患者 ID は薬局用にしか無い欄なので
// 薬局用の行の下で打つ (持ち主はお客様。次の記録からも埋まる)。Menu でなく popover なのは
// この欄を置くため。
const ExportRows = ({
  eventId,
  report,
  customer,
  onDone,
}: Props & { onDone: () => void }) => {
  const { showToast } = useToast()
  const saveCustomer = useUpdateNutritionCustomer(customer.id)
  const [downloading, setDownloading] = useState(false)

  const { fields, stamp } = report
  const store = fields.referring_store ?? ''
  const savedId = patientIdFor(customer.patient_ids, store)
  const [patientId, setPatientId] = useState(savedId)
  const [seenId, setSeenId] = useState(savedId)
  if (savedId !== seenId) {
    setSeenId(savedId)
    setPatientId(savedId)
  }

  // 変わっていれば保存する。失敗の知らせは hook の toast。
  const commitPatientId = async () => {
    if (patientId.trim() === savedId) return
    await saveCustomer.mutateAsync({
      patient_ids: withPatientId(customer.patient_ids, store, patientId),
    })
  }

  const download = async (destination: NutritionDestination, label: string) => {
    setDownloading(true)
    try {
      // 薬局用は打ったばかりの ID を先に保存してから落とす (back が保存済みの ID を差し込む)
      if (destination === 'pharmacy') {
        try {
          await commitPatientId()
        } catch {
          return
        }
      }
      const blob = await downloadNutritionReportXlsx(eventId, destination)
      downloadBlob(
        blob,
        exportFilename(label, fields.name ?? '', stamp.recorded_on)
      )
      onDone()
    } catch {
      showToast('ダウンロードできませんでした', 'error')
    } finally {
      setDownloading(false)
    }
  }

  const detail: Record<NutritionDestination, string> = {
    medical: [
      fields.institution,
      fields.physician ? `${fields.physician} 先生` : '',
    ]
      .filter(Boolean)
      .join(' '),
    pharmacy: store ? `${store} 店` : '',
    cafe: '',
  }

  return (
    <Stack>
      {report.intake?.stale && (
        // 食事の欄を直した後で計算し直していない回は、back が栄養価のシートを入れない (yakureki-back log/272 決定 8 の B1)
        <Typography
          sx={{
            px: 1,
            py: 0.75,
            fontSize: scaledFontSize('tag'),
            color: colorTokens.fg.muted,
          }}
        >
          食事の欄を直した後、栄養価をまだ計算し直していないため、栄養価のシートは入りません。
        </Typography>
      )}
      {NUTRITION_DESTINATIONS.map(({ key, label }) => (
        <Stack key={key}>
          <ButtonBase
            onClick={() => void download(key, label)}
            disabled={downloading}
            aria-label={`${label}をダウンロード`}
            sx={{
              justifyContent: 'flex-start',
              gap: 1,
              minHeight: 32,
              px: 1,
              borderRadius: `${RADIUS.md}px`,
              '&:hover, &.Mui-focusVisible': {
                bgcolor: colorTokens.bg.muted,
              },
              '&.Mui-disabled': { opacity: 0.5 },
            }}
          >
            <Typography
              component="span"
              sx={{
                flexShrink: 0,
                fontSize: scaledFontSize('bodySm'),
                color: colorTokens.fg.default,
              }}
            >
              {label}
            </Typography>
            <Typography
              component="span"
              noWrap
              sx={{
                ml: 'auto',
                minWidth: 0,
                fontSize: scaledFontSize('label'),
                color: colorTokens.fg.muted,
              }}
            >
              {detail[key]}
            </Typography>
          </ButtonBase>
          {key === 'pharmacy' && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ pl: 2, pr: 1, pb: 0.5 }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: scaledFontSize('label'),
                  color: colorTokens.fg.muted,
                }}
              >
                患者 ID
              </Typography>
              <InputBase
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                onBlur={() => void commitPatientId().catch(() => undefined)}
                placeholder="-"
                inputProps={{ 'aria-label': '患者 ID (薬局用)' }}
                sx={{ ...fieldInputSx, py: 0.25 }}
              />
            </Stack>
          )}
        </Stack>
      ))}
    </Stack>
  )
}
